<template>
    <div :id="wrapperName" ref="wrapper" class="vrm--map__gps">
        <vrm-mapbox
            :location="initialCoordinates"
            :geolocate-controls="false"
            @load="onMapboxLoaded"
        ></vrm-mapbox>
    </div>
</template>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import { AngularDirective } from '@/vue/decorators/angular-interop';
import VrmMapbox from '@/vue/components/map/VrmMapbox.vue';
import { parseOldGeofenceShape } from '@/vue/components/map/utils/geofence';
import {
    mapLayerGeofenceFill,
    mapLayerGeofenceFillOutline,
    mapLayerGeofenceOutline,
    mapLayerGpsPathLine,
    mapLayerIndividualEV,
    mapLayerIndividualInstallation,
} from '@/vue/components/map/constants';

@Component({
    components: { VrmMapbox },
})
@AngularDirective('vrm-map-gps-widget')
@AngularLocaleBundle('core')
export default class VrmMapGpsWidget extends Vue {
    /**
     * Coordinates of the installation marker.
     */
    @Prop()
    coords;

    /**
     * Geofence data if it exists.
     */
    @Prop()
    geofence;

    /**
     * GPS path data if it exists.
     */
    @Prop()
    gpsPath;

    /**
     * Used for the EV marker on the map
     */
    @Prop()
    isEv;

    /**
     * Internally used geofence data copy.
     *
     * Used to disconnect the map shape from the component prop and prevent unwanted map updates when data changes.
     *
     * @type {null}
     */
    existingGeofence = null;

    initialCoordinates = {
        lat: 0,
        lng: 0,
        zoom: 10,
        bearing: 0,
        pitch: 0,
    };

    initialized = false;

    observer = null;

    markerSource = 'installation';

    created() {
        this.initialCoordinates = {
            lat: this.coords ? this.coords.latitude : 0,
            lng: this.coords ? this.coords.longitude : 0,
            zoom: 7,
            bearing: 0,
            pitch: 0,
        };

        if (this.geofence) {
            this.updateGeofence(this.geofence);
        }
    }

    mounted() {
        this.observer = new ResizeObserver(this.onResize);
        this.observer.observe(this.$refs.wrapper);
    }

    // eslint-disable-next-line no-unused-vars
    onResize(e) {
        if (this.map) {
            this.map.resize();
        }
    }

    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
        if (this.observer) {
            this.observer.unobserve(this.$refs.wrapper);
        }
    }

    get wrapperName() {
        return 'gps-map-wrapper-' + this._uid;
    }

    @Watch('coords', { deep: true })
    // eslint-disable-next-line no-unused-vars
    onCoordsChanged(newValue, oldValue) {
        const zoom = this.map && this.map.getZoom() ? this.map.getZoom() : 7;
        const bearing = this.map && this.map.getBearing() ? this.map.getBearing() : 0;
        const pitch = this.map && this.map.getPitch() ? this.map.getPitch() : 0;

        this.initialCoordinates = {
            lat: newValue.latitude,
            lng: newValue.longitude,
            zoom: zoom,
            bearing: bearing,
            pitch: pitch,
        };

        if (this.map) {
            this.map.getSource(this.markerSource).setData({
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [newValue.longitude, newValue.latitude],
                        },
                    },
                ],
            });

            if (!this.initialized) {
                this.map.jumpTo([this.initialCoordinates.lng, this.initialCoordinates.lat]);

                this.initialized = true;
            }
        }
    }

    /**
     * Called whenever the controller passes a new geofence to the map.
     *
     * If a valid new value is received, parse it to GeoJSON and update the map.
     * If the geofence is removed, remove it from MapboxGL Draw as well.
     *
     * @param newValue
     * @param oldValue
     */
    @Watch('geofence')
    onGeofenceChanged(newValue, oldValue) {
        if (newValue) {
            this.updateGeofence(this.geofence);
        } else if (!newValue && oldValue) {
            this.existingGeofence = null;
        }

        if (this.map) {
            this.map.getSource('geofence').setData({
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: this.existingGeofence ? [this.existingGeofence] : [],
                },
            });
        }
    }

    /**
     * Called whenever the controller passes a new GPS path to the map.
     *
     * If a valid new GeoJSON value is received, update the map.
     *
     * @param newValue
     * @param oldValue
     */
    @Watch('gpsPath', { deep: true })
    // eslint-disable-next-line no-unused-vars
    onGpsPathChanged(newValue, oldValue) {
        if (!this.map) return;

        if (newValue && typeof newValue === 'object') {
            this.map.getSource('gpsPath').setData(newValue);
        } else {
            // clear path if no/invalid gpsPath
            this.map.getSource('gpsPath').setData({
                type: 'FeatureCollection',
                features: [],
            });
        }
    }

    /**
     * Called whenever the Mapbox map is initialized and loaded.
     *
     * Used to set up the map layers and features.
     *
     * @param map
     */
    onMapboxLoaded(map) {
        this.map = map;
        if (this.isEv) {
            this.markerSource = 'EV';
        }

        let geofenceSource = [];
        if (this.existingGeofence) {
            geofenceSource = [this.existingGeofence];
        }

        this.map.addSource('geofence', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: geofenceSource,
            },
        });

        this.map.addSource('gpsPath', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [],
            },
        });

        this.map.addSource(this.markerSource, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [this.coords.longitude, this.coords.latitude],
                        },
                    },
                ],
            },
        });

        // In case the gpsPath was received from the API before onMapboxLoaded(), initialise the gpsPath here
        if (this.gpsPath) {
            this.map.getSource('gpsPath').setData(this.gpsPath);
        }

        this.map.addLayer(mapLayerGeofenceFill);
        this.map.addLayer(mapLayerGeofenceFillOutline);
        this.map.addLayer(mapLayerGeofenceOutline);
        this.map.addLayer(mapLayerGpsPathLine);
        // Add a Symbol layer for individual installation/EV markers that are not in a cluster.
        if (this.isEv) {
            this.map.addLayer(mapLayerIndividualEV);
        } else {
            this.map.addLayer(mapLayerIndividualInstallation);
        }

        this.map.resize();
    }

    updateGeofence(geofence) {
        const shape = parseOldGeofenceShape(geofence);
        if (shape) {
            this.existingGeofence = shape;
        }
    }
}
</script>
